import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { PrivanaI18nProvider } from '@/i18n/PrivanaI18nProvider'
import { isSupportedLocale, resolveEffectiveLocale } from '../../../shared/locales.mjs'

const PREFIX = 'privana_member_locale_v1:'
export function memberLocaleScope({ clubCode, subject, organization, context }) {
  if (!clubCode || !subject || !organization) return null
  return PREFIX + JSON.stringify([context, clubCode, subject, organization])
}
function readPreference(scope) {
  if (!scope) return null
  try {
    const cached = JSON.parse(window.localStorage.getItem(scope))
    return cached?.scope === scope && isSupportedLocale(cached.locale) ? cached : null
  } catch { return null }
}
export function readMemberLocale(scope) { return readPreference(scope)?.locale || null }
function writePreference(scope, locale, profile) {
  if (!scope || !isSupportedLocale(locale)) return
  const preferredLocale = isSupportedLocale(profile?.preferred_locale) ? profile.preferred_locale : null
  try { window.localStorage.setItem(scope, JSON.stringify({ scope, locale, preferredLocale })) } catch { /* Optional cache. */ }
}

// Canonical bootstrap supplies authority once. Locale is never a controller key
// or a dependency of authentication, booking reads, holds or subscriptions.
export default function MemberLocaleBoundary({ scope, profile, settled, clubLocale, children }) {
  const cached = useMemo(() => readPreference(scope), [scope])
  const [override, setOverride] = useState(null)
  const authoritative = profile && Object.hasOwn(profile, 'preferred_locale')
  const cachedLocale = cached?.preferredLocale === null && isSupportedLocale(clubLocale)
    ? clubLocale : cached?.locale
  const base = authoritative
    ? resolveEffectiveLocale({ individualLocale: profile.preferred_locale, clubLocale: profile.club_default_locale || clubLocale })
    : cachedLocale || resolveEffectiveLocale({ clubLocale })
  const locale = override?.scope === scope ? override.locale : base
  const change = useCallback(next => {
    if (!scope) return
    setOverride({ scope, locale: next })
    writePreference(scope, next, authoritative && next === base ? profile : { preferred_locale: next })
  }, [scope, authoritative, profile, base])
  useLayoutEffect(() => { if (authoritative) writePreference(scope, base, profile) }, [scope, authoritative, profile, base])
  const ready = Boolean(profile || cached || settled)
  return <PrivanaI18nProvider resolvedLocale={locale} onLocaleChange={change} localeManaged>
    <div style={{ display: 'contents', visibility: ready ? undefined : 'hidden' }} aria-busy={!ready} inert={ready ? undefined : ''}>{children}</div>
  </PrivanaI18nProvider>
}
